import React from "react";
import ModalWrapper from "./ModalWrapper";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";

interface Announcement {
  id: number;
  title: string;
  body?: string;
  image?: string;
  cta1_label?: string;
  cta1_url?: string;
  cta2_label?: string;
  cta2_url?: string;
  close_label?: string;
  allow_close?: boolean;
  allow_dismiss?: boolean;
  allow_snooze?: boolean;
  modal_alignment?: "center" | "left";
}

interface AnnouncementDialogProps {
  announcement: Announcement;
  onClose: () => void;
  onShown: () => Promise<void>;
  onCloseAnnouncement: () => Promise<void>;
  onDismiss: () => Promise<void>;
  onSnooze: () => Promise<void>;
  onCtaClick: (cta: 1 | 2) => Promise<{ dismissed?: boolean } | void>;
}

const AnnouncementDialog: React.FC<AnnouncementDialogProps> = ({
  announcement,
  onClose,
  onShown,
  onCloseAnnouncement,
  onDismiss,
  onSnooze,
  onCtaClick,
}) => {
  const navigate = useNavigate();
  const TOUR_TRIGGER_PATH = "/start-tour";

  React.useEffect(() => {
    void onShown();
  }, [onShown]);

  const handleClose = async () => {
    await onCloseAnnouncement();
    onClose();
  };
  const handleNavigate = (url: string, inApp: boolean) => {
    if (url === TOUR_TRIGGER_PATH) {
      window.dispatchEvent(new CustomEvent("start-help-tour"));
      return;
    }
    if (inApp) {
      navigate(url);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const bodyLength = announcement.body?.length ?? 0;
  const modalSize =
    announcement.image || bodyLength > 400
      ? "lg"
      : bodyLength > 180
      ? "md"
      : "sm";
  const modalAlignment = announcement.modal_alignment ?? "center";
  const headerAlignmentClass =
    modalAlignment === "left" ? "tw-justify-start" : "tw-justify-center";
  const bodyAlignmentClass =
    modalAlignment === "left" ? "tw-text-left" : "tw-text-center";
  const footerClassName =
    modalAlignment === "left"
      ? "flex flex-wrap items-center gap-3 w-full"
      : "flex flex-col items-center gap-3 w-full";
  const footerActionsClassName =
    modalAlignment === "left"
      ? "flex flex-wrap justify-end gap-2 ml-auto"
      : "flex flex-col items-center gap-2 w-full mt-4";

  return (
    <ModalWrapper
      onClose={announcement.allow_close === false ? onClose : handleClose}
      title={announcement.title}
      size={modalSize}
      contentAlignment={modalAlignment}
      showCloseButton={announcement.allow_close !== false}
      closeOnOutsideClick={announcement.allow_close !== false}
      headerExtra={
        announcement.image ? (
          <div className={`tw-flex ${headerAlignmentClass}`}>
            <img
              src={announcement.image}
              alt=""
              className="w-full max-w-[10rem] rounded-lg max-h-40 object-contain"
            />
          </div>
        ) : undefined
      }
      footer={
        <div className={footerClassName}>
          {announcement.allow_snooze !== false && (
            <button
              onClick={async () => {
                await onSnooze();
                onClose();
              }}
              className="_button_19rto_40 _secondary_19rto_163 _outlined_19rto_69 text-sm px-3 py-1.5"
            >
              Später erinnern
            </button>
          )}

          <div className={footerActionsClassName}>
            {announcement.cta1_label && announcement.cta1_url && (
              <button
                type="button"
                onClick={async () => {
                  const clickResult = await onCtaClick(1);
                  const url = announcement.cta1_url as string;
                  const inApp = url.startsWith("/");
                  handleNavigate(url, inApp);
                  if (inApp || clickResult?.dismissed || url === TOUR_TRIGGER_PATH) {
                    onClose();
                  }
                }}
                className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80"
              >
                {announcement.cta1_label}
              </button>
            )}

            {announcement.cta2_label && announcement.cta2_url && (
              <button
                type="button"
                onClick={async () => {
                  const clickResult = await onCtaClick(2);
                  const url = announcement.cta2_url as string;
                  const inApp = url.startsWith("/");
                  handleNavigate(url, inApp);
                  if (inApp || clickResult?.dismissed || url === TOUR_TRIGGER_PATH) {
                    onClose();
                  }
                }}
                className="_button_19rto_40 _secondary_19rto_163 _outlined_19rto_69"
              >
                {announcement.cta2_label}
              </button>
            )}

            {announcement.close_label && (
              <button
                onClick={handleClose}
                className="_button_19rto_40 _secondary_19rto_163 _outlined_19rto_69"
              >
                {announcement.close_label}
              </button>
            )}

            {announcement.allow_dismiss !== false && (
              <button
                type="button"
                onClick={async () => {
                  await onDismiss();
                  onClose();
                }}
                className="_button_19rto_40 _secondary_19rto_163 _outlined_19rto_69"
              >
                Nicht mehr anzeigen
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* Inhalt */}
      <div className={`flex flex-col gap-5 ${bodyAlignmentClass}`}>
        {announcement.body && (
          <div className="prose max-w-none text-[1.02rem] leading-7">
            <ReactMarkdown
              components={{
                a: ({ href, children }) => {
                  const isExternal = Boolean(href && /^https?:\/\//.test(href));
                  return (
                    <a
                      href={href}
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noopener noreferrer" : undefined}
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {announcement.body}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};

export default AnnouncementDialog;
